import {useTranslation} from "react-i18next";
import React from "react";
import {
    Box,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow, Typography
} from "@mui/material";
import {API_URL, COLLECTION_ROLE} from "../../../constants";
import {makeRequest} from "../../../services/requestService";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";

const ITEMS_PER_PAGE = 10;

async function fetchUserCollections(userId) {
    const url = `${API_URL}/user/${userId}/collections/`;
    const response = await makeRequest(url);

    if (response.status !== 200) {
        throw response;
    }
    console.log(response.data);
    return {collections: response.data.data, totalItems: response.data.totalItems};
}

async function deleteMember(collectionId, userId) {
    const url = `${API_URL}/collection/${collectionId}/user/${userId}`;
    const response = await makeRequest(url, 'DELETE');
    return response.data;
}

async function putMember(collectionId, userId, role) {
    const data = {role};
    const url = `${API_URL}/collection/${collectionId}/user/${userId}`;
    const response = await makeRequest(url, 'PUT', data, {
        'Content-Type': 'application/json',
    });

    return response.data;
}

export default function CollectionsTab(props) {
    const {t} = useTranslation("common");
    const [totalItems, setTotalItems] = React.useState(-1);
    const [page, setPage] = React.useState(0);
    const [collections, setCollections] = React.useState(new Map());
    const [updateCollections, setUpdateCollections] = React.useState(0);

    React.useEffect(async () => {
        const resp = await fetchUserCollections(props.user.id);
        resp.collections.forEach((item) => {
            item.original = {...item}
            if (collections && collections.has(item.id)) {
                item.role = collections.get(item.id).role;
            }
        });
        setCollections(resp.collections.reduce((acc, cur) => acc.set(cur.collection.id, cur), new Map()));
        setTotalItems(resp.totalItems);
    }, [props.user.id, updateCollections]);

    async function handleDeleteCollection(collectionId) {
        await deleteMember(collectionId, props.user.id);
        setUpdateCollections(updateCollections + 1);

    }

    async function handleUpdateCollection(collectionId, role) {
        await putMember(collectionId, props.user.id, role);
        setUpdateCollections(updateCollections + 1);
    }

    return (
        <Box>
            <Typography variant="h5" mb={2}>
                {t('main.pages.admin.user.tabs.collections.title')}
            </Typography>


            <Paper>
                <TableContainer sx={{maxHeight: 599}}>
                    <Table stickyHeader size="small" aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key="id"></TableCell>
                                <TableCell key="name">
                                    {t('main.pages.admin.user.tabs.collections.table.name')}
                                </TableCell>
                                <TableCell key="description">
                                    {t('main.pages.admin.user.tabs.collections.table.description')}
                                </TableCell>
                                <TableCell key="role">
                                    {t('main.pages.admin.user.tabs.collections.table.role')}
                                </TableCell>
                                <TableCell key="actions"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...collections.values()].map((row) => (
                                <TableRow key={row.collection.id} hover role="checkbox" tabIndex={-1}
                                          sx={{'&:last-child td, &:last-child th': {border: 0}}}>
                                    <TableCell>
                                        #{row.collection.id}
                                    </TableCell>
                                    <TableCell>
                                        {row.collection.name}
                                    </TableCell>
                                    <TableCell>
                                        {row.collection.description}
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            key="role"
                                            size="small"
                                            sx={{width: 200}}
                                            disabled={row.role === COLLECTION_ROLE.OWNER}
                                            value={row.role}
                                            onChange={(event) => {
                                                collections.get(row.collection.id).role = event.target.value;
                                                setCollections(new Map(collections));
                                            }}
                                        >
                                            {Object.values(COLLECTION_ROLE).map((val) => (
                                                <MenuItem key={val} value={val}
                                                          disabled={val === COLLECTION_ROLE.OWNER}>
                                                    {t(`main.collections.role.${val.toLowerCase()}`)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        {row.role !== COLLECTION_ROLE.OWNER &&
                                            <IconButton onClick={() => handleDeleteCollection(row.collection.id)}>
                                                <DeleteIcon color="error"/>
                                            </IconButton>
                                        }
                                        <IconButton
                                            disabled={row.role === row.original.role}
                                            onClick={() => handleUpdateCollection(row.collection.id, row.role)}
                                        >
                                            <DoneIcon/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    size="small"
                    rowsPerPageOptions={[ITEMS_PER_PAGE]}
                    component="div"
                    count={totalItems}
                    rowsPerPage={ITEMS_PER_PAGE}
                    page={page}
                    onPageChange={(_, page) => setPage(page)}
                />
            </Paper>
        </Box>
    );
}